import RightArrow from "assets/images/v2/right-arrow.svg";
import tasksChecklistAnimation from "assets/images/v2/tasks-checklist.gif";
import TogglePills from "components/v2/toggle-pills/TogglePills";
import "./Tasks.scss";
import { useSelector, useDispatch } from "react-redux";
import {
  getSelectedTaskId,
  setSelectedTaskId,
  getTaskFilter,
  setTaskFilter,
  TaskFilterType
} from "features/authSlice";
import { useEffect, useState } from "react";
import { PendingItem, NeedStatus, Document, NeedType } from "../../dashboardService";
import FileUploadePanel from "components/v2/file-upload-panel/FileUploadPanel";
import { Spinner } from "components/v2/spinner/spinner";
import useDesktopChecker from "hooks/useDesktopChecker";
import useTabletChecker from "hooks/useTabletChecker";
import { LoadingPageProps } from "pages/dashboard/v2/loadingPageProps";
import { TASK_CARD_TITLE, TASK_CARD_HEADING_TEXT } from "utilities/Constant";
import Pill from "components/v2/pill/pill";
import { isNonEmptyString } from "utilities/string";
import ESignPanel from "pages/dashboard/home/v2/eSign-panel/ESignPanel";  
import Verification from "pages/dashboard/v2/verification-post-app/verification/Verification";
import { VerificationType } from "pages/online-verification/verificationService";
import { getTaskStatus, getTaskStatusForCount } from "utilities/needsHelper";

interface TaskProps {
  title: string;
  subTitle: string;
  isSelected?: boolean;
  isComments?: boolean;
  handleClick?: () => void;
}

const Task = (props: TaskProps) => {
  const { title, subTitle, handleClick, isSelected, isComments } = props;
  const isDesktop = useDesktopChecker();
  const isTablet = useTabletChecker();
  return (
    <button className={`task-item ${(isSelected && (isDesktop || isTablet)) ? "selected" : ""}`} onClick={handleClick}>
      <div className="task-item__text">
        <div className="task-item__title paragraph-medium-medium">{title}</div>
        <div className="task-item__pills-container">
          {isNonEmptyString(subTitle) && (
            <div className="task-item__subtitle">
              <Pill text={subTitle} variant="assigned" />
            </div>
          )}
          {isComments && (
            <div className="task-item__comments">
              <Pill text="New Comments" variant="comments" />
            </div>
          )}
        </div>
      </div>
      {!isDesktop && <img src={RightArrow} alt="Right arrow" />}
    </button>
  );
};

export interface TasksPageProps extends LoadingPageProps {
  isNeedsListLoading: boolean;
  needsList: PendingItem[];
}

const Tasks: React.FC<TasksPageProps> = ({ onLoad, isNeedsListLoading, needsList = [] }: TasksPageProps) => {
  const isDesktop = useDesktopChecker();
  const isTablet = useTabletChecker();
  const [showUpload, setShowUpload] = useState(false);
  const [selectedTask, setSelectedTask] =
    useState<{
      needId: string;
      title: string;
      subTitle?: string;
      assignedTo?: string;
      note?: string;
      documents?: Document[];
      needType?: NeedType;
      status?: string;
      needStatus?: NeedStatus;
      lat?: string;
      packageId?: string;
      isEnabled?: boolean;
    } | null>(null);
  const [showVerificationDrawer, setShowVerificationDrawer] = useState(true);
  const [selectFirstAfterSubmit, setSelectFirstAfterSubmit] = useState(false);
  const [taskFilters, setTaskFilters] = useState<{ label: string; value: TaskFilterType; badge?: string; }[]>([
    { label: "To Do", value: TaskFilterType.ToDo },
    { label: "Under Review", value: TaskFilterType.UnderReview },
    { label: "Complete", value: TaskFilterType.Complete },
  ]);
  
  const selectedTaskIdFromRedux = useSelector(getSelectedTaskId);
  const taskFilter = useSelector(getTaskFilter);
  const dispatch = useDispatch();

  const getHeader = () => {
    const titleClassName = "heading-medium";
    const title = <h5 className={titleClassName}>{TASK_CARD_TITLE}</h5>;
    const heading = <span className="label-large-regular">{TASK_CARD_HEADING_TEXT}</span>;

    return (
      <div className="tasks__heading">
        {title}
        <section className="tasks__subhead">{heading}</section>
      </div>
    );
  };

  const handleTaskClick = (item: PendingItem) => {
    dispatch(setSelectedTaskId(item.id));
    setSelectedTask({
      needId: item.id,
      title: item.title,
      assignedTo: item.assignedTo,
      note: item.note,
      status: item.status,
      needType: item.needType,
      documents: item.documents,
      needStatus: item.needStatus,
      lat: item.lat,
      packageId: item.packageId,
      isEnabled: item.isEnabled,
    });

    if (!isDesktop) {
      setShowUpload(true);
    }

    if (item.needType === NeedType.Voa) {
      setShowVerificationDrawer(true);
    }
  };

  useEffect(() => {
    setShowUpload(isDesktop);
  }, [isDesktop]);

  const filteredNeedsList = needsList.filter((item) => taskFilter === getTaskStatus(item));

  useEffect(() => {
    // Recalculate filtered list inside the effect to avoid unstable dependency
    const list = needsList.filter(item => taskFilter === getTaskStatus(item));

    if (list.length === 0) {
      if (selectedTask !== null) setSelectedTask(null);
      return;
    }

    // If selected task does not exist in this tab, auto-select first task
    const isValid =
      selectedTask &&
      list.some(t => t.id === selectedTask.needId);

    if (!isValid) {
      const first = list[0];
      setSelectedTask({
        needId: first.id,
        title: first.title,
        assignedTo: first.assignedTo,
        note: first.note,
        status: first.status,
        needType: first.needType,
        documents: first.documents,
        needStatus: first.needStatus,
        lat: first.lat,
        packageId: first.packageId,
        isEnabled: first.isEnabled,
      });
    }
  }, [taskFilter, needsList]);

  // Handle selected task from Redux (when coming from TaskCard)
  useEffect(() => {
    if (selectedTaskIdFromRedux && filteredNeedsList.length > 0) {
      const taskFromRedux = filteredNeedsList.find((item) => item.id === selectedTaskIdFromRedux);
      if (taskFromRedux) {
        setSelectedTask({
          needId: taskFromRedux.id,
          title: taskFromRedux.title,
          assignedTo: taskFromRedux.assignedTo,
          note: taskFromRedux.note,
          status: taskFromRedux.status,
          needType: taskFromRedux.needType,
          documents: taskFromRedux.documents,
          needStatus: taskFromRedux.needStatus,
          lat: taskFromRedux.lat,
          packageId: taskFromRedux.packageId,
          isEnabled: taskFromRedux.isEnabled,
        });

        // Show upload panel on mobile when coming from TaskCard
        if (!isDesktop) {
          setShowUpload(true);
        }

        // Clear the Redux selection after setting local state
        dispatch(setSelectedTaskId(undefined));
      }
    }
  }, [selectedTaskIdFromRedux, filteredNeedsList, dispatch, isDesktop]);

  // Handle default selection when no specific task is selected
  useEffect(() => {
    const visibleList = filteredNeedsList;
    let timeoutId: ReturnType<typeof setTimeout> | undefined = undefined;
    //set timeout to allow toast for 3 seconds before making any selection.
    if (selectFirstAfterSubmit) {
      timeoutId = setTimeout(() => {
        if (visibleList.length > 0) {
          const firstItem = visibleList[0];
          setSelectedTask({
            needId: firstItem.id,
            title: firstItem.title,
            assignedTo: firstItem.assignedTo,
            note: firstItem.note,
            status: firstItem.status,
            needType: firstItem.needType,
            documents: firstItem.documents,
            needStatus: firstItem.needStatus,
            lat: firstItem.lat,
            packageId: firstItem.packageId,
            isEnabled: firstItem.isEnabled,
          });
        }
      setSelectFirstAfterSubmit(false); // clear flag after handling
      }, 3000);
    }
    // Only set default if we have tasks, no current selection, and no Redux selection
    else if (needsList.length > 0 && !selectedTask && !selectedTaskIdFromRedux) {
      const firstItem = needsList[0];
      setSelectedTask({
        needId: firstItem.id,
        title: firstItem.title,
        assignedTo: firstItem.assignedTo,
        note: firstItem.note,
        status: firstItem.status,
        needType: firstItem.needType,
        documents: firstItem.documents,
        needStatus: firstItem.needStatus,
        lat: firstItem.lat,
        packageId: firstItem.packageId,
        isEnabled: firstItem.isEnabled,
      });
    }
    if (needsList.length > 0) {
      const newTaskFilters = taskFilters.map(tf => {
        const matchingNeeds = needsList.filter(item => getTaskStatusForCount(item) === tf.value).length;
        let newTaskFilter = { ...tf, badge: "" };
        if (tf.value === TaskFilterType.ToDo) {
          newTaskFilter = {
            ...newTaskFilter,
            badge: matchingNeeds > 0 ? matchingNeeds.toString() : ""
          };
        }
        return newTaskFilter;
      });
      setTaskFilters(newTaskFilters);
    }
    else if (visibleList.length === 0){
      // if there are no needs, clear selection
        setSelectedTask(null);
    }
    return () => {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [needsList, selectFirstAfterSubmit, selectedTaskIdFromRedux, dispatch]);

  useEffect(() => {
    onLoad?.();
  }, []);

  const handleClosedVerificationDrawer = () => {
    if(!isDesktop) setShowUpload(false);
    setShowVerificationDrawer(false);
  }

    // To simulate VOA completion, to be removed later
  const handleVerificationSuccess = () => {
    if (selectedTask) {
      setSelectedTask({...selectedTask, status: 'completed' });
    }
  };

  return (
    <main className="tasks-main">
      {(isDesktop || !showUpload || isTablet) && (
        <section className={`tasks ${showUpload ? "hidden-mobile" : ""}`}>
          {getHeader()}
          <section className="tasks__current-complete">
            <TogglePills options={taskFilters} value={taskFilter} onChange={(value) => dispatch(setTaskFilter(value))} />
          </section>
          <section className="tasks__task-items">
            {isNeedsListLoading && (
              <div className="spinner">
                <Spinner isApi />
              </div>
            )}
            {filteredNeedsList.map((item, idx) => {
              const isSelected = selectedTask?.needId === item.id;
              return (
                <Task
                  key={idx}
                  title={item.title}
                  subTitle={item.assignedTo ?? ""}
                  isSelected={isSelected}
                  isComments={item.status === "needsAttention" || !!item.note?.trim()}
                  handleClick={() => {
                    handleTaskClick(item);
                  }}
                />
              );
            })}
            {isDesktop && filteredNeedsList.length === 0 && (
              <div className="tasks__no-selected-tasks paragraph-medium-medium">
                {taskFilter === TaskFilterType.Complete ? "No completed tasks yet" : "No current tasks"}
              </div>
            )}
          </section>
        </section>
        )}
          <section className="tasks__selected">
            {filteredNeedsList.length === 0 && (
              <section className="tasks__no-tasks">
                <img src={tasksChecklistAnimation} alt="todo list being checked off with a pencil" />
                <span className="paragraph-medium">
                  {taskFilter === TaskFilterType.ToDo
                    ? "When you have tasks you will be able to view more details here."
                    : taskFilter === TaskFilterType.Complete
                      ? "Your completed tasks will show up here"
                      : ""}
                </span>
              </section>
            )}
        {(isDesktop || showUpload) && filteredNeedsList.length > 0 && !isNeedsListLoading && (
              selectedTask?.needType === NeedType.ESign ? (
                <ESignPanel
                  status={selectedTask.status || ""}
                  loanAccessToken={selectedTask.lat}
                  packageId={selectedTask.packageId}
                  setShowUpload={setShowUpload}
                  isEnabled={selectedTask.isEnabled}
                />
              ) : (
                selectedTask?.needType !== NeedType.Voa && (
                  <FileUploadePanel
                    needId={selectedTask?.needId || ""}
                    title={selectedTask?.title || ""}
                    assignedTo={selectedTask?.assignedTo}
                    setShowUpload={setShowUpload}
                    note={selectedTask?.note}
                    documents={selectedTask?.documents}
                    onSubmitSuccess={() => {
                      setSelectFirstAfterSubmit(true);
                    }}
                  />
                )
              ))}
            {selectedTask?.needType === NeedType.Voa && showVerificationDrawer && (
              <div className="verification-container">
                <Verification
                  verificationType={VerificationType.VOA}
                  closeDrawerForMobile={handleClosedVerificationDrawer}
                  status={selectedTask?.status || ""}
                  handlesuccessfullVerification={handleVerificationSuccess}
                />
              </div>
            )}
          </section>
    </main>
  );
};

export default Tasks;